import { useCallback, useMemo, useState } from 'react'
import { useOrg } from '@/admin/hooks/use-org'
import type { ProductFilterValue } from '@/shared/components/ui/product-filter-menu'

/**
 * Persist a table's filter fly-out selection to localStorage, partitioned per
 * tenant + table, so the selected facets survive a page refresh.
 *
 * Mirrors ``useTableColumnPreferences`` (same storage discipline: refuse to
 * read/write without a tenant slug to avoid cross-tenant bleed). The value is
 * a ``ProductFilterValue`` (facet key -> selected option values), the same
 * shape ``ProductFilterMenu`` consumes.
 */

const KEY_PREFIX = 'raven:table-filter-preferences:v1'

function getStorageKey(tableId: string, tenantSlug: string | null): string | null {
  if (!tenantSlug) return null
  return `${KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function readFilterValues(storageKey: string | null): ProductFilterValue {
  if (typeof window === 'undefined' || !storageKey) return {}
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    // Keep only string[] entries so a corrupt payload can't poison the params.
    const clean: ProductFilterValue = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
        clean[key] = value as string[]
      }
    }
    return clean
  } catch {
    return {}
  }
}

function writeFilterValues(storageKey: string | null, values: ProductFilterValue) {
  if (typeof window === 'undefined' || !storageKey) return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(values))
  } catch {
    // Ignore storage failures so the filter keeps working in private mode.
  }
}

export function useTableFilterPreferences(
  tableId: string,
): [ProductFilterValue, (values: ProductFilterValue) => void] {
  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null

  const storageKey = useMemo(
    () => getStorageKey(tableId, tenantSlug),
    [tableId, tenantSlug],
  )

  const [filterValues, setFilterValuesState] = useState<ProductFilterValue>(() =>
    readFilterValues(storageKey),
  )

  // Re-hydrate during render when the tenant/table key changes.
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey)
  if (storageKey !== prevStorageKey) {
    setPrevStorageKey(storageKey)
    setFilterValuesState(readFilterValues(storageKey))
  }

  const setFilterValues = useCallback(
    (values: ProductFilterValue) => {
      setFilterValuesState(values)
      writeFilterValues(storageKey, values)
    },
    [storageKey],
  )

  return [filterValues, setFilterValues]
}
